import React, { useState } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { formatCurrency, formatDate, exportToCSV } from '../utils/helpers';
import AddTransactionModal from './AddTransactionModal';

function TransactionRow({ transaction, isAdmin, onEdit, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const isIncome = transaction.type === 'income';

  return (
    <div
      className={`tx-row ${hovered ? 'tx-row--hovered' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={`tx-icon ${isIncome ? 'tx-icon--income' : 'tx-icon--expense'}`}>
        {isIncome ? '↓' : '↑'}
      </div>

      <div className="tx-info">
        <div className="tx-info__top">
          <span className="tx-info__desc">{transaction.description}</span>
          <span className="tx-badge">{transaction.category}</span>
        </div>
        <span className="tx-info__date">{formatDate(transaction.date)}</span>
      </div>

      <span className={`tx-amount ${isIncome ? 'tx-amount--income' : 'tx-amount--expense'}`}>
        {isIncome ? '+' : '-'}{formatCurrency(transaction.amount)}
      </span>

      {isAdmin && (
        <div className={`tx-actions ${hovered ? 'tx-actions--visible' : ''}`}>
          <button className="icon-btn icon-btn--edit" onClick={() => onEdit(transaction)} title="Edit">✏️</button>
          <button className="icon-btn icon-btn--delete" onClick={() => onDelete(transaction.id)} title="Delete">🗑</button>
        </div>
      )}
    </div>
  );
}

function TransactionsList({ onToast }) {
  const { transactions, userRole, deleteTransaction } = useDashboard();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [search, setSearch] = useState('');

  const categories = [...new Set(transactions.map((t) => t.category))];

  const filtered = transactions.filter((t) => {
    if (typeFilter !== 'all' && t.type !== typeFilter) return false;
    if (catFilter !== 'all' && t.category !== catFilter) return false;
    if (
      search &&
      !t.description.toLowerCase().includes(search.toLowerCase()) &&
      !t.category.toLowerCase().includes(search.toLowerCase())
    ) return false;
    return true;
  });

  const openEdit = (t) => { setEditingTxn(t); setIsModalOpen(true); };
  const closeModal = () => { setIsModalOpen(false); setEditingTxn(null); };

  const handleDelete = (id) => {
    deleteTransaction(id);
    onToast('Transaction deleted!');
  };

  const handleExport = () => {
    exportToCSV(filtered);
    onToast('Transactions exported!');
  };

  return (
    <>
      <div className="card">
        {/* Header */}
        <div className="tx-list-header">
          <div>
            <h3 className="card__title">Recent Transactions</h3>
            <p className="tx-count">{filtered.length} transaction{filtered.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="tx-list-actions">
            <button className="btn btn--outline" onClick={handleExport}>⬇ Export</button>
            {userRole === 'admin' && (
              <button className="btn btn--primary" onClick={() => setIsModalOpen(true)}>
                + Add Transaction
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="filters">
          <div className="filter-search">
            <span className="filter-search__icon">🔍</span>
            <input
              className="form-input form-input--search"
              placeholder="Search transactions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select className="form-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All Types</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>

          <select className="form-select" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
            <option value="all">All Categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* List */}
        <div className="tx-list">
          {filtered.length > 0 ? (
            filtered.map((t) => (
              <TransactionRow
                key={t.id}
                transaction={t}
                isAdmin={userRole === 'admin'}
                onEdit={openEdit}
                onDelete={handleDelete}
              />
            ))
          ) : (
            <div className="tx-empty">No transactions found</div>
          )}
        </div>
      </div>

      <AddTransactionModal
        isOpen={isModalOpen}
        onClose={closeModal}
        transaction={editingTxn}
        onToast={onToast}
      />
    </>
  );
}

export default TransactionsList;
